import { useMemo } from 'react'
import { diffDays } from '../../shared/date'
import { symptomInsights, type Cycle } from '../../shared/predict'
import { Card, SectionTitle } from '../components/ui'
import { useI18n } from '../i18n'
import { useReady } from '../store'

export function Stats() {
  const { t } = useI18n()
  const { snapshot, prediction } = useReady()
  const { cycleLength, periodLength, cycles } = prediction

  const insights = useMemo(
    () => symptomInsights(cycles, snapshot.symptoms).slice(0, 5),
    [cycles, snapshot.symptoms],
  )

  const intimacyThisCycle = prediction.currentCycleStart
    ? snapshot.intimacy.filter(
        (i) => diffDays(prediction.currentCycleStart!, i.date) >= 0,
      ).length
    : 0

  const completed = cycles.filter((c): c is Cycle & { length: number } => c.length !== null)

  return (
    <div className="px-4 pb-6 space-y-5">
      <header className="safe-top pb-1 px-1">
        <h1 className="text-2xl font-semibold">{t.stats.title}</h1>
      </header>

      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label={t.stats.cycleLength}
          value={cycleLength.mean.toFixed(1)}
          sub={`${t.regularity[cycleLength.regularity]} · ±${cycleLength.sd.toFixed(1)} ${t.home.dayUnit(
            cycleLength.sd,
          )}`}
        />
        <StatCard
          label={t.stats.periodLength}
          value={periodLength.mean.toFixed(1)}
          sub={`±${periodLength.sd.toFixed(1)} ${t.home.dayUnit(periodLength.sd)}`}
        />
      </div>

      <div>
        <SectionTitle>{t.stats.recentCycles}</SectionTitle>
        <Card>
          {completed.length < 2 ? (
            <p className="text-sm text-muted py-4 text-center">{t.stats.noEnoughData}</p>
          ) : (
            <CycleChart cycles={completed} mean={cycleLength.mean} sd={cycleLength.sd} />
          )}
        </Card>
      </div>

      <div>
        <SectionTitle>{t.stats.insightsTitle}</SectionTitle>
        <Card>
          {insights.length === 0 ? (
            <p className="text-sm text-muted py-4 text-center leading-relaxed">
              {t.stats.insightsEmpty}
            </p>
          ) : (
            <ul className="space-y-3">
              {insights.map((ins) => {
                const name = t.symptom[ins.symptom as keyof typeof t.symptom] ?? ins.symptom
                // 经期前后出现的症状，说成「经期前几天」比「周期第几天」好懂得多
                const text =
                  ins.daysBeforePeriod > 0 && ins.daysBeforePeriod <= 7
                    ? t.stats.insightBefore(name, ins.daysBeforePeriod)
                    : t.stats.insightLine(name, ins.typicalCycleDay)
                return (
                  <li key={ins.symptom} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                    <span className="text-[15px] leading-relaxed">{text}</span>
                  </li>
                )
              })}
            </ul>
          )}
        </Card>
      </div>

      <div>
        <SectionTitle>{t.stats.intimacyCount}</SectionTitle>
        <Card>
          <p className="text-[15px]">{t.stats.timesThisCycle(intimacyThisCycle)}</p>
        </Card>
      </div>
    </div>
  )
}

function StatCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  const { t } = useI18n()
  return (
    <div className="rounded-3xl bg-card border border-line/70 p-4">
      <p className="text-xs text-muted">{label}</p>
      <p className="text-3xl font-semibold mt-1.5 tabular-nums">
        {value}
        <span className="text-sm font-normal text-muted ml-1">{t.home.dayUnit(Number(value))}</span>
      </p>
      <p className="text-[11px] text-muted mt-1">{sub}</p>
    </div>
  )
}

/**
 * 周期长度趋势。横线是平均值，浅色带是 ±1 个标准差 ——
 * 点都落在带子里说明规律，散得开就说明波动大，比只看一个平均数直观。
 */
function CycleChart({
  cycles,
  mean,
  sd,
}: {
  cycles: (Cycle & { length: number })[]
  mean: number
  sd: number
}) {
  const { formatShort } = useI18n()
  const shown = cycles.slice(-8)
  const W = 300
  const H = 130
  const PAD = { top: 12, bottom: 22, left: 26, right: 8 }

  const values = shown.map((c) => c.length)
  const lo = Math.min(...values, mean - sd) - 2
  const hi = Math.max(...values, mean + sd) + 2
  const y = (v: number) =>
    PAD.top + (1 - (v - lo) / (hi - lo)) * (H - PAD.top - PAD.bottom)
  const x = (i: number) =>
    PAD.left +
    (shown.length === 1 ? 0.5 : i / (shown.length - 1)) * (W - PAD.left - PAD.right)

  const line = shown.map((c, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(c.length)}`).join(' ')

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[280px]" role="img">
        {/* ±1σ 带 */}
        <rect
          x={PAD.left}
          y={y(mean + sd)}
          width={W - PAD.left - PAD.right}
          height={Math.max(1, y(mean - sd) - y(mean + sd))}
          fill="var(--c-accent)"
          opacity={0.1}
          rx={4}
        />
        {/* 平均线 */}
        <line
          x1={PAD.left}
          x2={W - PAD.right}
          y1={y(mean)}
          y2={y(mean)}
          stroke="var(--c-accent)"
          strokeWidth={1}
          strokeDasharray="3 3"
          opacity={0.6}
        />
        <text
          x={PAD.left - 6}
          y={y(mean) + 3.5}
          textAnchor="end"
          fontSize="9"
          fill="var(--c-muted)"
        >
          {mean.toFixed(0)}
        </text>

        <path d={line} fill="none" stroke="var(--c-accent)" strokeWidth={2} strokeLinejoin="round" />

        {shown.map((c, i) => (
          <g key={c.start}>
            <circle
              cx={x(i)}
              cy={y(c.length)}
              r={c.anomalous ? 3 : 4}
              fill={c.anomalous ? 'var(--c-bg)' : 'var(--c-accent)'}
              stroke="var(--c-accent)"
              strokeWidth={c.anomalous ? 1.5 : 0}
            />
            <text
              x={x(i)}
              y={H - 6}
              textAnchor="middle"
              fontSize="9"
              fill="var(--c-muted)"
            >
              {formatShort(c.start)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  )
}
